import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Link, useNavigate } from "react-router-dom";
import { HashLink } from 'react-router-hash-link';
import axios from "axios";
import { isAuth, signout, getCokie } from "../helpers/auth";

export default function AppNavbar() {
  const navigate = useNavigate();
  const navbarRef = useRef(null);

  // Estados estructurados
  const [isExpanded, setIsExpanded] = useState(false);
  const [hasError, setHasError] = useState(false);
  const [user, setUser] = useState(null);
  const [nameInitial, setNameInitial] = useState('');
  const [imageSrc, setImageSrc] = useState(null);

  // Evaluar hosting una sola vez de forma eficiente
  const isEsfapaHostname = window.location.hostname === "www.esfapa.edu.pe";

  // 1. Petición HTTP memorizada para evitar re-creación de funciones
  const getUser = useCallback(async () => {
    const authData = isAuth();
    if (!authData?._id) return;

    try {
      const token = getCokie("token");
      const { data } = await axios.get(
        `${process.env.REACT_APP_API_URL}/users/user/${authData._id}`,
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );

      setUser(data);
      if (data?.name) {
        setNameInitial(data.name.charAt(0).toUpperCase());
      }
      localStorage.setItem("user", JSON.stringify(data));
    } catch (err) {
      if (err.response?.status === 401) {
        signout(() => {
          navigate("/login");
        });
      }
    }
  }, [navigate]);

  // 2. Efecto único de inicialización y manejo de eventos
  useEffect(() => {
    // Carga dinámica optimizada del SVG del día
    const currentDay = new Date().getDay();
    if (isEsfapaHostname) {
      import(`../assests/www${currentDay + 1}.svg`)
        .then((image) => setImageSrc(image.default))
        .catch((err) => console.error("Failed to load navbar image", err));
    } else {
      import(`../assests/logo.svg`)
        .then((image) => setImageSrc(image.default))
        .catch((err) => console.error("Failed to load navbar image", err));
    }
    if (isAuth()) {
      getUser();
    }

    // Handlers optimizados para cerrar el menú desplegable
    const handleClickOutside = (event) => {
      if (navbarRef.current && !navbarRef.current.contains(event.target)) {
        setIsExpanded(false);
      }
    };

    const handleScroll = () => {
      if (isExpanded) setIsExpanded(false);
    };

    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('scroll', handleScroll, { passive: true });

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('scroll', handleScroll);
    };
  }, [getUser, isExpanded]);

  return (
    <nav className="navbar navbar-expand-lg navbar-light bg-light text-uppercase" ref={navbarRef}>
      <div className="container-fluid">

        {/* Brand Logo */}
        <div className="navbar-brand text-uppercase text-center p-0">
          <Link to="/">
            {imageSrc && (
              <img className="m-auto wrapperestperfil" src={imageSrc} alt="Logo" />
            )}
          </Link>
        </div>

        {/* Toggle button */}
        <button
          className="navbar-toggler btn"
          type="button"
          aria-controls="navbarNav"
          aria-expanded={isExpanded}
          aria-label="Toggle navigation"
          onClick={() => setIsExpanded(prev => !prev)}
        >
          <span className="navbar-toggler-icon"></span>
        </button>

        {/* Collapsible menu */}
        <div className={`collapse navbar-collapse ${isExpanded ? 'show' : ''}`} id="navbarNav">
          <ul className="navbar-nav ml-auto">
            {isEsfapaHostname && (
              <li className="nav-item">
                <HashLink className="nav-link" to="/#section-one">
                  Documentos
                </HashLink>
              </li>
            )}

            {isAuth() ? (
              <li className="nav-item">
                <Link to="/carpeta" className="nav-link">
                  PANEL
                </Link>
              </li>
            ) : (
              <li className="nav-item">
                <Link to="/login" className="nav-link">
                  Iniciar
                </Link>
              </li>
            )}
            <li className="nav-item">
              <Link to="/cursos" className="nav-link">
                Cursos
              </Link>
            </li>
            <li className="nav-item">
              <Link to="/carreras" className="nav-link">
                Carreras
              </Link>
            </li>
          </ul>

          {/* User Profile Info */}
          {isAuth() && user && (
            <Link to="/private" className="shadow-none">
              {hasError ? (
                <div className="wrapperestperfil name">{nameInitial}</div>
              ) : (
                <img
                  className="wrapperestperfil"
                  src={`${process.env.REACT_APP_URL}/collections/${user.foto}`}
                  alt="Perfil"
                  onError={() => setHasError(true)}
                />
              )}
            </Link>
          )}
        </div>

      </div>
    </nav>
  );
}